import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  InputAdornment,
  Stack,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
  Link,
  Paper,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Search,
  ArrowForward,
  Description,
  Terminal,
  Security,
  MenuBook,
  Build,
  School,
  NavigateNext,
  Home,
  TrendingUp,
  Speed,
  Shield,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import CorporateLayout from '../components/CorporateLayout';
import { getAllDocumentationSections } from '../data/documentation';

const CorporateDocumentationPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const docSections = getAllDocumentationSections();

  const filteredSections = React.useMemo(() => {
    if (!searchTerm) return docSections;
    
    return docSections.filter(section =>
      section.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      section.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, docSections]);

  const getSectionIcon = (key) => {
    const iconMap = {
      'getting-started': <Speed sx={{ fontSize: 40 }} />,
      'cli': <Terminal sx={{ fontSize: 40 }} />,
      'security': <Security sx={{ fontSize: 40 }} />,
      'user-guide': <MenuBook sx={{ fontSize: 40 }} />,
      'developers': <Build sx={{ fontSize: 40 }} />,
      'tutorials': <School sx={{ fontSize: 40 }} />,
    };
    return iconMap[key] || <MenuBook sx={{ fontSize: 40 }} />;
  };

  const quickStartSteps = [
    {
      title: '1. Install PWGen-Rust',
      description: 'Download and install PWGen-Rust on your platform',
      link: '/docs/getting-started/installation',
      icon: <Description color="primary" />,
    },
    {
      title: '2. First Setup',
      description: 'Create your master password and secure vault',
      link: '/docs/getting-started/first-run',
      icon: <Shield color="primary" />,
    },
    {
      title: '3. Basic Usage',
      description: 'Learn the fundamentals of password management',
      link: '/docs/getting-started/basic-usage',
      icon: <TrendingUp color="primary" />,
    },
  ];

  return (
    <CorporateLayout>
      {/* Breadcrumbs */}
      <Breadcrumbs 
        separator={<NavigateNext fontSize="small" />} 
        sx={{ mb: 4 }}
      >
        <Link
          component={RouterLink}
          to="/"
          sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', textDecoration: 'none' }}
        >
          <Home sx={{ mr: 0.5, fontSize: 18 }} />
          Home
        </Link>
        <Typography color="text.primary" sx={{ fontWeight: 500 }}>
          Documentation
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography variant="h2" component="h1" sx={{ mb: 2, fontWeight: 600 }}>
          PWGen-Rust Documentation
        </Typography>
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 4, maxWidth: 600, mx: 'auto' }}>
          Comprehensive guides and API documentation to help you get the most out of PWGen-Rust's 
          zero-knowledge password management platform.
        </Typography>
        
        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" sx={{ mb: 4 }}>
          <Chip label="Zero-Knowledge" color="primary" />
          <Chip label="Open Source" color="primary" variant="outlined" />
          <Chip label="Rust-Powered" color="secondary" />
          <Chip label="Cross-Platform" color="secondary" variant="outlined" />
        </Stack>

        {/* Search */}
        <TextField
          placeholder="Search documentation..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ maxWidth: 500, width: '100%' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Quick Start Section */}
      <Paper elevation={0} sx={{ p: 4, mb: 6, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, textAlign: 'center' }}>
          Quick Start Guide
        </Typography>
        <Grid container spacing={3}>
          {quickStartSteps.map((step, index) => (
            <Grid size={{ xs: 12, md: 4 }} key={index}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%', 
                  border: '1px solid', 
                  borderColor: 'divider',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: 2,
                  }
                }}
              >
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Box sx={{ mb: 2, color: 'primary.main' }}>
                    {step.icon}
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                  <Button
                    component={RouterLink}
                    to={step.link}
                    variant="contained"
                    size="small"
                    disableElevation
                  >
                    Get Started
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Documentation Sections */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" sx={{ mb: 2, fontWeight: 700 }}>
          Complete Documentation
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Explore our comprehensive guides and references
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {filteredSections.map((section, index) => (
          <Grid size={{ xs: 12, md: 6 }} key={section.key}>
            <Card 
              elevation={0}
              sx={{ 
                height: '100%',
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: 2,
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ 
                    p: 1, 
                    borderRadius: 2, 
                    backgroundColor: 'primary.main', 
                    color: 'primary.contrastText',
                    mr: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {getSectionIcon(section.key)}
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {section.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {section.description}
                    </Typography>
                  </Box>
                </Box>

                {/* Pages List */}
                <List dense>
                  {section.pages.slice(0, 4).map((page) => (
                    <ListItem key={page} disablePadding>
                      <ListItemButton
                        component={RouterLink}
                        to={`/docs/${section.key}/${page}`}
                        sx={{ 
                          borderRadius: 1,
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <ArrowForward sx={{ fontSize: 16 }} color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={page.split('-').map(word => 
                            word.charAt(0).toUpperCase() + word.slice(1)
                          ).join(' ')}
                          primaryTypographyProps={{
                            variant: 'body2',
                            fontWeight: 500,
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                  {section.pages.length > 4 && (
                    <ListItem>
                      <ListItemText 
                        primary={`+${section.pages.length - 4} more pages`}
                        primaryTypographyProps={{
                          variant: 'caption',
                          color: 'text.secondary',
                          fontStyle: 'italic',
                        }}
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
              
              <CardActions sx={{ p: 3, pt: 0 }}>
                <Button
                  component={RouterLink}
                  to={`/docs/${section.key}/${section.pages[0]}`}
                  variant="outlined"
                  endIcon={<ArrowForward />}
                  fullWidth
                >
                  Explore {section.title}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* No Results */}
      {filteredSections.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
            No documentation found
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4 }}>
            Try adjusting your search terms
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setSearchTerm('')}
          >
            Clear Search
          </Button>
        </Box>
      )}

      {/* Additional Resources */}
      <Box sx={{ mt: 8 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
          Additional Resources
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Community
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Join discussions, ask questions, and get help from the community.
                </Typography>
                <Button
                  href="https://github.com/KiefStudioMA/PWGen-Rust/discussions"
                  target="_blank"
                  variant="outlined"
                  fullWidth
                >
                  Join Discussion
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Examples
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Real-world examples and code snippets for developers.
                </Typography>
                <Button
                  href="https://github.com/KiefStudioMA/PWGen-Rust/tree/main/examples"
                  target="_blank"
                  variant="outlined"
                  fullWidth
                >
                  View Examples
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Contributing
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Help improve PWGen-Rust by contributing to the project.
                </Typography>
                <Button
                  href="https://github.com/KiefStudioMA/PWGen-Rust/blob/main/CONTRIBUTING.md"
                  target="_blank"
                  variant="outlined"
                  fullWidth
                >
                  Contribute
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </CorporateLayout>
  );
};

export default CorporateDocumentationPage;